export type DashboardType = 'gtm' | 'customer' | 'finances' | 'product';

export interface WorkflowStage {
  stage: number;
  label: string;
  color: 'slate' | 'blue' | 'purple' | 'amber' | 'emerald' | 'red' | 'pink';
}

export interface DashboardWorkflow {
  stages: WorkflowStage[];
  cardTypes: number[];
}

export interface CustomerSegmentConfig {
  icon: string;
  name: string;
  stages: WorkflowStage[];
  cardTypes: string[];
  addButtonLabel: string;
  addButtonCardType: number;
}

export interface CustomerWorkflow {
  segments: {
    audience: CustomerSegmentConfig;
    pipeline: CustomerSegmentConfig;
    accounts: CustomerSegmentConfig;
  };
}

// GTM Workflow Configuration
export const GTM_WORKFLOW: DashboardWorkflow = {
  stages: [
    { stage: 0, label: 'Ideation', color: 'slate' },
    { stage: 1, label: 'Drafting', color: 'blue' },
    { stage: 2, label: 'Review', color: 'purple' },
    { stage: 3, label: 'Scheduled', color: 'amber' },
    { stage: 4, label: 'Published', color: 'emerald' },
  ],
  cardTypes: [1, 2, 3, 4, 5], // Whitepaper, Blog, Podcast, Landing Page, Email Campaign
};

// Customer Dashboard Workflow Configuration
export const CUSTOMER_WORKFLOW: CustomerWorkflow = {
  segments: {
    audience: {
      icon: '📧',
      name: 'Audience',
      stages: [
        { stage: 0, label: 'New', color: 'slate' },
        { stage: 1, label: 'Nurturing', color: 'blue' },
        { stage: 2, label: 'Engaged', color: 'purple' },
        { stage: 3, label: 'Converted', color: 'emerald' },
      ],
      cardTypes: ['subscriber', 'registrant'],
      addButtonLabel: 'Add New',
      addButtonCardType: 10,
    },
    pipeline: {
      icon: '🎯',
      name: 'Pipeline',
      stages: [
        { stage: 0, label: 'New', color: 'slate' },
        { stage: 1, label: 'Contacted', color: 'blue' },
        { stage: 2, label: 'Discovery', color: 'purple' },
        { stage: 3, label: 'Proposal', color: 'amber' },
        { stage: 4, label: 'Won', color: 'emerald' },
      ],
      cardTypes: ['demo', 'trial', 'lead', 'referral', 'inquiry'],
      addButtonLabel: 'Add New',
      addButtonCardType: 10,
    },
    accounts: {
      icon: '⭐',
      name: 'Accounts',
      stages: [
        { stage: 0, label: 'Onboarding', color: 'blue' },
        { stage: 1, label: 'Active', color: 'emerald' },
        { stage: 2, label: 'At Risk', color: 'amber' },
        { stage: 3, label: 'Churned', color: 'slate' },
      ],
      cardTypes: ['account'],
      addButtonLabel: 'Add Account',
      addButtonCardType: 9,
    },
  },
};

// Product Timeline Workflow Stages
export const PRODUCT_TIMELINE_STAGES: WorkflowStage[] = [
  { stage: 0, label: 'Planned', color: 'slate' },
  { stage: 1, label: 'In Progress', color: 'blue' },
  { stage: 2, label: 'Testing', color: 'purple' },
  { stage: 3, label: 'Complete', color: 'emerald' },
];

// Product Timeline Card Type Configuration
export interface ProductCardTypeConfig {
  id: number;
  label: string;
  icon: string;
  badge: string;
  borderColor: string;
}

export const PRODUCT_CARD_TYPES: ProductCardTypeConfig[] = [
  { id: 11, label: 'Product Feature', icon: '⚡', badge: 'bg-blue-500/20 text-blue-300', borderColor: 'border-blue-400' },
  { id: 12, label: 'Data Import', icon: '📊', badge: 'bg-purple-500/20 text-purple-300', borderColor: 'border-purple-400' },
  { id: 13, label: 'Manufacturer', icon: '🏭', badge: 'bg-amber-500/20 text-amber-300', borderColor: 'border-amber-400' },
  { id: 14, label: 'Distributor', icon: '🚚', badge: 'bg-emerald-500/20 text-emerald-300', borderColor: 'border-emerald-400' },
  { id: 15, label: 'Product Line', icon: '📦', badge: 'bg-pink-500/20 text-pink-300', borderColor: 'border-pink-400' },
];

export const PRODUCT_TIMELINE_WORKFLOW: DashboardWorkflow = {
  stages: PRODUCT_TIMELINE_STAGES,
  cardTypes: [11, 12, 13, 14, 15],
};

// Timeline Quarter Utilities
export interface TimelineQuarter {
  key: string;
  label: string;
  isCurrent: boolean;
}

export function getTimelineQuarters(): TimelineQuarter[] {
  const now = new Date();
  const currentQuarter = Math.ceil((now.getMonth() + 1) / 3);
  const currentYear = now.getFullYear();

  const quarters: TimelineQuarter[] = [];
  for (let i = 0; i < 4; i++) {
    let q = currentQuarter + i;
    let y = currentYear;
    while (q > 4) { q -= 4; y += 1; }
    const key = `Q${q} ${y}`;
    quarters.push({ key, label: key, isCurrent: i === 0 });
  }
  return quarters;
}

// Centralized card type definitions
export type CardType =
  | 'whitepaper' | 'blog' | 'podcast' | 'landing_page' | 'campaign'                       // GTM
  | 'client' | 'opportunity'                                                               // Customer (legacy)
  | 'subscriber' | 'registrant' | 'demo' | 'trial' | 'lead' | 'referral' | 'inquiry' | 'account'  // Customer
  | 'grant' | 'accelerator' | 'vc_contact' | 'vc'                                         // Finances
  | 'product_feature' | 'data_import' | 'manufacturer' | 'distributor' | 'product_line';  // Product

export interface DashboardCardTypeConfig {
  id: number;
  label: string;
}

export const DASHBOARD_CARD_TYPES: Record<DashboardType, DashboardCardTypeConfig[]> = {
  gtm: [
    { id: 1, label: 'Whitepaper' },
    { id: 2, label: 'Blog' },
    { id: 3, label: 'Podcast' },
    { id: 4, label: 'Landing Page' },
    { id: 5, label: 'Email Campaign' },
  ],
  customer: [
    { id: 9, label: 'Client' },
    { id: 10, label: 'Opportunity' },
  ],
  finances: [
    { id: 6, label: 'Grant' },
    { id: 7, label: 'Accelerator' },
    { id: 8, label: 'VC Contact' },
  ],
  product: [
    { id: 11, label: 'Product Feature' },
    { id: 12, label: 'Data Import' },
    { id: 13, label: 'Manufacturer' },
    { id: 14, label: 'Distributor' },
    { id: 15, label: 'Product Line' },
  ],
};

// Dashboard number mapping
export const DASHBOARD_NUMBERS: Record<DashboardType, number> = {
  gtm: 0,
  customer: 1,
  finances: 2,
  product: 3,
};

// Get dashboard workflow
export function getDashboardWorkflow(dashboardType: DashboardType): DashboardWorkflow | CustomerWorkflow {
  if (dashboardType === 'gtm') {
    return GTM_WORKFLOW;
  }
  if (dashboardType === 'customer') {
    return CUSTOMER_WORKFLOW;
  }
  if (dashboardType === 'product') {
    return PRODUCT_TIMELINE_WORKFLOW;
  }
  return GTM_WORKFLOW; // Default fallback
}
